import React from 'react'
import PropTypes from 'prop-types'
import {Box, Flex} from '@chakra-ui/react'
import {Component} from '@salesforce/commerce-sdk-react/components'

/**
 * PDCEditoBigTile is a React functional component that renders a layout of components
 * within a flexible container. It is designed to display a list of components with
 * specific styling and layout properties.
 *
 * @param {Object} props - The properties object.
 * @param {Array} props.productList - An array of products to be associated with each component.
 * @param {Object} props.category - The category object to be associated with each component.
 * @param {Array} props.regions - An array of regions, where the first region's components are used for rendering.
 *
 * @returns {JSX.Element} A JSX element representing a flex container with a list of components.
 *
 * The component uses the following layout properties:
 * - Centers its children horizontally using `justifyContent`.
 * - Applies horizontal margins (`mx`) and vertical padding (`pt` and `pb`) with responsive values.
 * - Maps over the `components` array from the first region, setting additional properties
 *   (`productList`, `category`, `imageSizes`) on each component before rendering.
 *
 * Each component is wrapped in a `Box` with responsive flex properties, ensuring that the layout
 * adapts to different screen sizes.
 */
export const PDCEditoBigTile = ({productList, category, layoutIndex, regions}) => {
    let components = []
    if (regions) {
        components = regions[0].components || []
    }

    return (
        <Flex
            justifyContent={'center'}
            mx={[-10, null, null, 'auto']}
            pt={[34, null, null, 70]}
            pb={['44px', null, null, 70]}
        >
            {components.map((component, index) => {
                component.productList = productList
                component.category = category
                component.imageSizes = ['100vw', '100vw', '100vw', '50vw', '50vw', '60vw']
                component.layoutIndex = layoutIndex
                return (
                    <Box
                        key={component?.id || index}
                        flex={['1 0 100%', null, 'initial']}
                        width={['100%', null, null, 'calc(50vw - 25px)', null, 'calc(60vw - 25px)']}
                        maxWidth={'1487px'}
                    >
                        <Component component={component} />
                    </Box>
                )
            })}
        </Flex>
    )
}

PDCEditoBigTile.propTypes = {
    productList: PropTypes.array,
    category: PropTypes.object,
    layoutIndex: PropTypes.number,
    regions: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            components: PropTypes.arrayOf(
                PropTypes.shape({
                    data: PropTypes.object,
                    id: PropTypes.string,
                    typeId: PropTypes.string
                })
            )
        })
    )
}
